import {IRange} from "@pg-design/range-module";
import {IOption} from "@pg-design/select-module";
import {areaFormat} from "@pg-mono/string-utils";
import {RegionType} from "@pg-rp/common-types";
import {AdditionalAreasType, getAdditionalAreasName} from "@pg-rp/homematch-tinder";

import {
    HOME_MATCH_PREFERENCES_SURVEY_BUILDING_HEIGHTS,
    HOME_MATCH_PREFERENCES_SURVEY_COMPLETION_DATES,
    HOME_MATCH_PREFERENCES_SURVEY_CONTENT,
    HOME_MATCH_PREFERENCES_SURVEY_FLOORS,
    HOME_MATCH_PREFERENCES_SURVEY_MARKETS
} from "../constants/preferences/home_match_preferences_survey_content";
import {
    HomeMatchFinancingPreference,
    HomeMatchFloorsPreference,
    HomeMatchPreferencesSurveyBuildingHeight,
    HomeMatchPreferencesSurveyPropertyType
} from "../constants/preferences/home_match_preferences_values";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../constants/preferences/home_match_preferences_wizard_content";
import {ConstructionEndDateType} from "../types/ConstructionEndDateType";
import {IHomeMatchSurveyApiRequest} from "../types/home_match_preferences";
import {getHomeMatchPreferencesSurveyMarkets, getHomeMatchPreferencesSurveyPropertyType} from "./get_home_match_preferences_survey_form_values";
import {getHomeMatchBasicBudgetText, getHomeMatchBasicRoomsText} from "./home_match_basic_preferences_flow_utils";
import {getRoomsRangeValues, RoomsRangeInput} from "./home_match_rooms_range";
import {parseRangeBound} from "./parse_range_bound";

const {SUMMARY} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;

export interface IHomeMatchPreferencesWizardSummaryRegion {
    id: number;
    name: string;
    parent: {name: string} | null;
    short_name: string;
    type: RegionType;
}

export interface IHomeMatchPreferencesWizardSummaryData {
    additionalAreas: number[] | null;
    area: IRange<number | null> | null;
    buildingHeight?: HomeMatchPreferencesSurveyBuildingHeight | null;
    constructionEndDate?: ConstructionEndDateType | null;
    floorsPreference?: HomeMatchFloorsPreference[] | null;
    marketTypes?: number[] | null;
    price: IHomeMatchSurveyApiRequest["price"];
    regionDetails: IHomeMatchPreferencesWizardSummaryRegion[];
    regionIds: number[];
    regionOptions: IOption<number>[];
    rooms: RoomsRangeInput;
    types?: number[] | null;
}

const FINANCING_VALUE: Partial<Record<HomeMatchFinancingPreference, string>> = {
    [HomeMatchFinancingPreference.CASH]: SUMMARY.FINANCING_CASH,
    [HomeMatchFinancingPreference.CREDIT]: SUMMARY.FINANCING_CREDIT
};

const getRegionLabel = (regionId: number, regionOptions: IOption<number>[], detail?: IHomeMatchPreferencesWizardSummaryRegion) =>
    regionOptions.find((region) => region.value === regionId)?.label || detail?.short_name || detail?.name;

const getDistrictName = (cityName: string, detail: IHomeMatchPreferencesWizardSummaryRegion | undefined, label: string) => {
    if (detail?.name) return detail.name;

    const cityPrefix = `${cityName}, `;

    return label.startsWith(cityPrefix) ? label.slice(cityPrefix.length) : label;
};

export const getLocationLabel = (regionIds: number[], regionOptions: IOption<number>[], regionDetails: IHomeMatchPreferencesWizardSummaryRegion[]) => {
    const detailsById = new Map(regionDetails.map((region) => [region.id, region]));
    const districtsByCity = new Map<string, string[]>();

    regionIds.forEach((regionId) => {
        const detail = detailsById.get(regionId);
        const cityName = detail?.type === RegionType.DISTRICT ? detail.parent?.name : undefined;
        const label = getRegionLabel(regionId, regionOptions, detail);

        if (!cityName || !label) return;

        const districts = districtsByCity.get(cityName) ?? [];
        districts.push(getDistrictName(cityName, detail, label));
        districtsByCity.set(cityName, districts);
    });

    const emittedCities = new Set<string>();
    const parts: string[] = [];

    regionIds.forEach((regionId) => {
        const detail = detailsById.get(regionId);
        const cityName = detail?.type === RegionType.DISTRICT ? detail.parent?.name : undefined;

        if (cityName) {
            if (emittedCities.has(cityName)) return;

            emittedCities.add(cityName);
            parts.push(`${cityName} — ${districtsByCity.get(cityName)?.join(", ")}`);
            return;
        }

        const label = getRegionLabel(regionId, regionOptions, detail);

        if (!label || districtsByCity.has(label)) return;

        parts.push(label);
    });

    return parts.join(", ");
};

export const getBudgetLabel = (price: IHomeMatchSurveyApiRequest["price"], financing: HomeMatchFinancingPreference | null) => {
    if (!price?.upper || price.upper <= 0) {
        return "";
    }

    const amount = getHomeMatchBasicBudgetText(price).replace(/^do\s+/, "");
    const financingValue = financing ? FINANCING_VALUE[financing] : "";

    return [amount, financingValue].filter(Boolean).join(" · ");
};

const optionLabel = <T extends string | number>(options: readonly {label: string; value: T}[], value: T | null | undefined) => {
    if (value == null) return "";

    return options.find((option) => option.value === value)?.label ?? "";
};

const getMarketSummaryLabel = (marketTypes: number[] | null | undefined) => {
    const markets = getHomeMatchPreferencesSurveyMarkets(marketTypes);

    if (markets.length > 1) return HOME_MATCH_PREFERENCES_SURVEY_CONTENT.MARKET_BOTH;

    return optionLabel(HOME_MATCH_PREFERENCES_SURVEY_MARKETS, markets[0]);
};

export const getSummaryPropertyLabel = (types: number[] | null | undefined) =>
    getHomeMatchPreferencesSurveyPropertyType(types) === HomeMatchPreferencesSurveyPropertyType.HOUSE ? SUMMARY.HOUSE : SUMMARY.APARTMENT;

export const getApartmentLabel = (
    summary: Pick<
        IHomeMatchPreferencesWizardSummaryData,
        "additionalAreas" | "area" | "buildingHeight" | "constructionEndDate" | "floorsPreference" | "marketTypes" | "rooms"
    >
) => {
    const {additionalAreas, area, buildingHeight, constructionEndDate, floorsPreference, marketTypes, rooms} = summary;
    const {lower, upper} = getRoomsRangeValues(rooms);
    const roomsLabel = rooms == null ? "" : getHomeMatchBasicRoomsText({lower, upper});
    const areaLower = parseRangeBound(area?.lower);
    const areaUpper = parseRangeBound(area?.upper);
    const areaLabel = areaLower || areaUpper ? areaFormat([areaLower ?? null, areaUpper ?? null]) : "";
    const extras = (additionalAreas ?? [])
        .map((type) => getAdditionalAreasName(type as AdditionalAreasType))
        .filter(Boolean)
        .join(", ");

    return [
        roomsLabel,
        areaLabel,
        extras,
        HOME_MATCH_PREFERENCES_SURVEY_FLOORS.filter((option) => floorsPreference?.includes(option.value))
            .map((option) => option.label)
            .join(", "),
        optionLabel(HOME_MATCH_PREFERENCES_SURVEY_BUILDING_HEIGHTS, buildingHeight),
        optionLabel(HOME_MATCH_PREFERENCES_SURVEY_COMPLETION_DATES, constructionEndDate),
        getMarketSummaryLabel(marketTypes)
    ]
        .filter(Boolean)
        .join(" · ");
};
import {IRange} from "@pg-design/range-module";
import {IOption} from "@pg-design/select-module";
import {areaFormat} from "@pg-mono/string-utils";
import {RegionType} from "@pg-rp/common-types";
import {AdditionalAreasType, getAdditionalAreasName} from "@pg-rp/homematch-tinder";

import {
    HOME_MATCH_PREFERENCES_SURVEY_BUILDING_HEIGHTS,
    HOME_MATCH_PREFERENCES_SURVEY_COMPLETION_DATES,
    HOME_MATCH_PREFERENCES_SURVEY_CONTENT,
    HOME_MATCH_PREFERENCES_SURVEY_FLOORS,
    HOME_MATCH_PREFERENCES_SURVEY_MARKETS
} from "../constants/preferences/home_match_preferences_survey_content";
import {
    HomeMatchFinancingPreference,
    HomeMatchFloorsPreference,
    HomeMatchPreferencesSurveyBuildingHeight,
    HomeMatchPreferencesSurveyPropertyType
} from "../constants/preferences/home_match_preferences_values";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../constants/preferences/home_match_preferences_wizard_content";
import {ConstructionEndDateType} from "../types/ConstructionEndDateType";
import {IHomeMatchSurveyApiRequest} from "../types/home_match_preferences";
import {getHomeMatchPreferencesSurveyMarkets, getHomeMatchPreferencesSurveyPropertyType} from "./get_home_match_preferences_survey_form_values";
import {getHomeMatchBasicBudgetText, getHomeMatchBasicRoomsText} from "./home_match_basic_preferences_flow_utils";
import {getRoomsRangeValues, RoomsRangeInput} from "./home_match_rooms_range";
import {parseRangeBound} from "./parse_range_bound";

const {SUMMARY} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;

export interface IHomeMatchPreferencesWizardSummaryRegion {
    id: number;
    name: string;
    parent: {name: string} | null;
    short_name: string;
    type: RegionType;
}

export interface IHomeMatchPreferencesWizardSummaryData {
    additionalAreas: number[] | null;
    area: IRange<number | null> | null;
    buildingHeight?: HomeMatchPreferencesSurveyBuildingHeight | null;
    constructionEndDate?: ConstructionEndDateType | null;
    floorsPreference?: HomeMatchFloorsPreference[] | null;
    marketTypes?: number[] | null;
    price: IHomeMatchSurveyApiRequest["price"];
    regionDetails: IHomeMatchPreferencesWizardSummaryRegion[];
    regionIds: number[];
    regionOptions: IOption<number>[];
    rooms: RoomsRangeInput;
    types?: number[] | null;
}

const FINANCING_VALUE: Partial<Record<HomeMatchFinancingPreference, string>> = {
    [HomeMatchFinancingPreference.CASH]: SUMMARY.FINANCING_CASH,
    [HomeMatchFinancingPreference.CREDIT]: SUMMARY.FINANCING_CREDIT
};

const getRegionLabel = (regionId: number, regionOptions: IOption<number>[], detail?: IHomeMatchPreferencesWizardSummaryRegion) =>
    regionOptions.find((region) => region.value === regionId)?.label || detail?.short_name || detail?.name;

const getDistrictName = (cityName: string, detail: IHomeMatchPreferencesWizardSummaryRegion | undefined, label: string) => {
    if (detail?.name) return detail.name;

    const cityPrefix = `${cityName}, `;

    return label.startsWith(cityPrefix) ? label.slice(cityPrefix.length) : label;
};

export const getLocationLabel = (regionIds: number[], regionOptions: IOption<number>[], regionDetails: IHomeMatchPreferencesWizardSummaryRegion[]) => {
    const detailsById = new Map(regionDetails.map((region) => [region.id, region]));
    const districtsByCity = new Map<string, string[]>();

    regionIds.forEach((regionId) => {
        const detail = detailsById.get(regionId);
        const cityName = detail?.type === RegionType.DISTRICT ? detail.parent?.name : undefined;
        const label = getRegionLabel(regionId, regionOptions, detail);

        if (!cityName || !label) return;

        const districts = districtsByCity.get(cityName) ?? [];
        districts.push(getDistrictName(cityName, detail, label));
        districtsByCity.set(cityName, districts);
    });

    const emittedCities = new Set<string>();
    const parts: string[] = [];

    regionIds.forEach((regionId) => {
        const detail = detailsById.get(regionId);
        const cityName = detail?.type === RegionType.DISTRICT ? detail.parent?.name : undefined;

        if (cityName) {
            if (emittedCities.has(cityName)) return;

            emittedCities.add(cityName);
            parts.push(`${cityName} — ${districtsByCity.get(cityName)?.join(", ")}`);
            return;
        }

        const label = getRegionLabel(regionId, regionOptions, detail);

        if (!label || districtsByCity.has(label)) return;

        parts.push(label);
    });

    return parts.join(", ");
};

export const getBudgetLabel = (price: IHomeMatchSurveyApiRequest["price"], financing: HomeMatchFinancingPreference | null) => {
    if (!price?.upper || price.upper <= 0) {
        return "";
    }

    const amount = getHomeMatchBasicBudgetText(price).replace(/^do\s+/, "");
    const financingValue = financing ? FINANCING_VALUE[financing] : "";

    return [amount, financingValue].filter(Boolean).join(" · ");
};

const optionLabel = <T extends string | number>(options: readonly {label: string; value: T}[], value: T | null | undefined) => {
    if (value == null) return "";

    return options.find((option) => option.value === value)?.label ?? "";
};

const getMarketSummaryLabel = (marketTypes: number[] | null | undefined) => {
    const markets = getHomeMatchPreferencesSurveyMarkets(marketTypes);

    if (markets.length > 1) return HOME_MATCH_PREFERENCES_SURVEY_CONTENT.MARKET_BOTH;

    return optionLabel(HOME_MATCH_PREFERENCES_SURVEY_MARKETS, markets[0]);
};

export const getSummaryPropertyLabel = (types: number[] | null | undefined) =>
    getHomeMatchPreferencesSurveyPropertyType(types) === HomeMatchPreferencesSurveyPropertyType.HOUSE ? SUMMARY.HOUSE : SUMMARY.APARTMENT;

export const getApartmentLabel = (
    summary: Pick<
        IHomeMatchPreferencesWizardSummaryData,
        "additionalAreas" | "area" | "buildingHeight" | "constructionEndDate" | "floorsPreference" | "marketTypes" | "rooms"
    >
) => {
    const {additionalAreas, area, buildingHeight, constructionEndDate, floorsPreference, marketTypes, rooms} = summary;
    const {lower, upper} = getRoomsRangeValues(rooms);
    const roomsLabel = rooms == null ? "" : getHomeMatchBasicRoomsText({lower, upper});
    const areaLower = parseRangeBound(area?.lower);
    const areaUpper = parseRangeBound(area?.upper);
    const areaLabel = areaLower || areaUpper ? areaFormat([areaLower ?? null, areaUpper ?? null]) : "";
    const extras = (additionalAreas ?? [])
        .map((type) => getAdditionalAreasName(type as AdditionalAreasType))
        .filter(Boolean)
        .join(", ");

    return [
        roomsLabel,
        areaLabel,
        extras,
        HOME_MATCH_PREFERENCES_SURVEY_FLOORS.filter((option) => floorsPreference?.includes(option.value))
            .map((option) => option.label)
            .join(", "),
        optionLabel(HOME_MATCH_PREFERENCES_SURVEY_BUILDING_HEIGHTS, buildingHeight),
        optionLabel(HOME_MATCH_PREFERENCES_SURVEY_COMPLETION_DATES, constructionEndDate),
        getMarketSummaryLabel(marketTypes)
    ]
        .filter(Boolean)
        .join(" · ");
};
